import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type { CreateFormRequest, FormQuery, UpdateFormRequest } from '@troia-v3/types';

import { checklistTemplatesApi } from '../services/checklistTemplatesApi';

/**
 * Hooks dos MODELOS de checklist (`/api/checklists/templates`).
 *
 * Espelham `useForms`, mas batem nas rotas do módulo checklists — que são
 * gateadas por `checklists:*` em vez de `forms:*`. Ver
 * `checklistTemplatesApi` para o porquê.
 *
 * As mutações invalidam TAMBÉM `forms-dropdown`: o Select "Modelo" do modal
 * "Novo checklist" lê aquela chave, e sem a invalidação o modelo recém-criado
 * só apareceria depois do staleTime de 5 min.
 */
const invalidate = (queryClient: ReturnType<typeof useQueryClient>): void => {
  void queryClient.invalidateQueries({ queryKey: ['checklist-templates'] });
  void queryClient.invalidateQueries({ queryKey: ['forms-dropdown'] });
};

export const useChecklistTemplates = (query?: FormQuery, enabled = true) => {
  return useQuery({
    queryKey: ['checklist-templates', query],
    queryFn: () => checklistTemplatesApi.getAll(query),
    staleTime: 30 * 1000,
    refetchOnWindowFocus: false,
    enabled,
  });
};

export const useChecklistTemplate = (id?: string, enabled = true) => {
  return useQuery({
    queryKey: ['checklist-template', id],
    queryFn: () => checklistTemplatesApi.getById(id as string),
    enabled: !!id && enabled,
    staleTime: 30 * 1000,
  });
};

export const useCreateChecklistTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateFormRequest) => checklistTemplatesApi.create(data),
    onSuccess: () => {
      invalidate(queryClient);
      toast.success('Modelo criado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao criar modelo');
    },
  });
};

export const useUpdateChecklistTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateFormRequest }) =>
      checklistTemplatesApi.update(id, data),
    onSuccess: (_, variables) => {
      invalidate(queryClient);
      void queryClient.invalidateQueries({ queryKey: ['checklist-template', variables.id] });
      toast.success('Modelo atualizado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao atualizar modelo');
    },
  });
};

export const useDeleteChecklistTemplate = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => checklistTemplatesApi.delete(id),
    onSuccess: () => {
      invalidate(queryClient);
      toast.success('Modelo removido com sucesso!');
    },
    // Sem `onError` de propósito: o interceptador global (`lib/api.ts`) já
    // mostra a mensagem do backend — inclusive a recusa de modelo em uso
    // ("...está em uso na automação X..."). Um toast aqui saía em dobro.
  });
};
